import { Button, DiagnosticCopy } from '@krp/ui-kit'

import type { DeliveryView } from '../model/deliveryView'
import type { AttentionRowVm } from '../model/attentionView'
import { DeliveryIndicator } from './DeliveryIndicator'
import './delivery.css'

/** Что известно про ведомость сервера: загрузка, ошибка целиком (с кодом и номером запроса) или список. */
export type DeliveryServerState =
  | { state: 'loading' }
  | { state: 'error'; message: string; code: string | null; status: number | null; correlationId: string | null }
  | { state: 'ready'; rows: readonly AttentionRowVm[] }

export interface DeliveryWindowProps {
  /** Локальный отчёт агента этого компьютера. */
  local: DeliveryView
  onRefreshLocal?: () => void
  server: DeliveryServerState
  showClosed: boolean
  onShowClosed: (value: boolean) => void
  onReloadServer: () => void
}

function errorReport(error: Extract<DeliveryServerState, { state: 'error' }>): string {
  return [
    'Ведомость доставки: не удалось прочитать',
    `Причина: ${error.message}`,
    error.code === null ? null : `Код: ${error.code}`,
    error.status === null ? null : `HTTP-статус: ${error.status}`,
    error.correlationId === null ? null : `Номер запроса: ${error.correlationId}`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n')
}

/**
 * Окно «Доставка» (RP-OWN9) для администратора: слева направо — что говорит агент этого компьютера и что требует человека по
 * данным центрального журнала (ведомость Resource.Receipts). Два источника показаны раздельно и не выводятся один из другого:
 * «агент доставил» и «сервер принял» — разные факты.
 */
export function DeliveryWindow({ local, onRefreshLocal, server, showClosed, onShowClosed, onReloadServer }: DeliveryWindowProps) {
  return (
    <div className="own-delivery-window">
      <h2>Доставка в центральный журнал</h2>
      <h3>Эта касса: что говорит агент</h3>
      <DeliveryIndicator view={local} onRefresh={onRefreshLocal} />
      <h3>Сервер: что требует внимания</h3>
      <label className="own-delivery__toggle">
        <input type="checkbox" checked={showClosed} onChange={(event) => onShowClosed(event.target.checked)} /> Показать и закрытые
      </label>
      {server.state === 'loading' && <p role="status">Читаем ведомость сервера…</p>}
      {server.state === 'error' && (
        <div role="alert" className="own-delivery own-delivery--problem">
          <p className="own-delivery__head">
            <span className="own-delivery__mark" aria-hidden="true">✖</span>
            <strong>Ведомость сервера прочитать не удалось</strong>
          </p>
          <p className="own-delivery__error-text">{server.message}</p>
          <DiagnosticCopy text={errorReport(server)} label="Скопировать" />
          <Button variant="ghost" onClick={onReloadServer}>Повторить чтение</Button>
        </div>
      )}
      {server.state === 'ready' && <AttentionList rows={server.rows} showClosed={showClosed} />}
    </div>
  )
}

function AttentionList({ rows, showClosed }: { rows: readonly AttentionRowVm[]; showClosed: boolean }) {
  if (rows.length === 0) {
    return <p role="status">{showClosed ? 'В ведомости записей нет.' : 'Открытых записей нет: сервер не ждёт разбора.'}</p>
  }
  return (
    <ul className="own-delivery__list">
      {rows.map((row) => (
        <li key={row.id} className={row.open ? 'own-delivery__item own-delivery__item--open' : 'own-delivery__item'}>
          <p>
            <strong>{row.kindLabel}</strong> — {row.stateText}
          </p>
          <p className="own-delivery__error-text">{row.text}</p>
          <ul className="own-delivery__meta">
            {row.meta.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <DiagnosticCopy text={row.report} label="Скопировать" />
        </li>
      ))}
    </ul>
  )
}
